import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import backend from '@api/backend'
import CustomerLocation from '@data/locations/model/customerLocation'
import {
  useAccountsQuery,
  useSelectedAccountIds,
} from '@data/accounts/accounts'
import { useLocationStore } from '@store/location.store'

export const LOCATIONS_QUERY_KEY = ['locations'] as const

const EMPTY_LOCATIONS: CustomerLocation[] = []

interface CustomerLocationSettingsResponse {
  id: number
  onboardingDate?: string | null
  onboardingStatus: boolean
  validFrom: string
  validTo: string
  modifiedByUserIdRef: number
}

export interface LocationApiResponse {
  createdAt?: string | null
  updatedAt?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  town?: string | null
  county?: string | null
  postcode?: string | null
  country: string
  id: number
  locationName: string
  accountIdRef: number
  companyAlias?: string | null
  occupants?: number | null
  active: boolean
  suspended: boolean
  settings: CustomerLocationSettingsResponse | null
}

const nullableString = (value: string | null | undefined) => value ?? ''

export const mapLocationResponse = (raw: LocationApiResponse): CustomerLocation => ({
  id: String(raw.id),
  name: raw.locationName,
  accountIdRef: String(raw.accountIdRef),
  companyAlias: raw.companyAlias ?? '',
  addressLine1: nullableString(raw.addressLine1),
  addressLine2: nullableString(raw.addressLine2),
  town: nullableString(raw.town),
  postcode: nullableString(raw.postcode),
  county: nullableString(raw.county),
  country: raw.country,
  active: raw.active,
  suspended: raw.suspended,
  settings: {
    onboardingDate: raw.settings?.onboardingDate ?? null,
    onboardingStatus: raw.settings?.onboardingStatus ?? false,
  },
})

export const useLocationsQuery = () =>
  useQuery<CustomerLocation[]>({
    queryKey: LOCATIONS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<LocationApiResponse[]>('/customer_locations', {
        signal,
      })
      return response.data.map(mapLocationResponse)
    },
    staleTime: Infinity,
  })

// The locations a selected account can currently show: active, and belonging to an account
// that is selected. Lifted from the facade's memo unchanged.
export const useVisibleLocations = () => {
  const { data } = useLocationsQuery()
  const allLocations = data ?? EMPTY_LOCATIONS
  const selectedAccountIds = useSelectedAccountIds()

  return useMemo(
    () =>
      allLocations.filter(
        (location) => location.active && selectedAccountIds.includes(location.accountIdRef ?? ''),
      ),
    [allLocations, selectedAccountIds],
  )
}

// `selectedLocationIds === null` means "all visible locations" — the all-selected state is
// derived rather than stored, so an account change cannot leave a stale explicit list.
export const useSelectedLocations = () => {
  const locations = useVisibleLocations()
  const selectedLocationIds = useLocationStore((state) => state.selectedLocationIds)

  return useMemo(
    () =>
      selectedLocationIds === null
        ? locations
        : locations.filter((location) => selectedLocationIds.includes(location.id ?? '')),
    [locations, selectedLocationIds],
  )
}

// Folded here rather than at the call site: locations are derived from accounts, so while
// `/accounts` is in flight the location list is not yet knowable and a consumer that read
// only the locations query would render "no locations" instead of "loading".
export const useLocationsLoading = () => {
  const { isLoading: accountsLoading } = useAccountsQuery()
  const { isLoading: locationsLoading } = useLocationsQuery()

  return accountsLoading || locationsLoading
}
